import { useCallback, useRef } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  MAX_BREAKDOWN_PROPERTIES,
  FunnelResultSchema,
  HeatmapPagesResponseSchema,
  HeatmapSnapshotResponseSchema,
  JobResponseSchema,
  LIVE_FEED_LIMIT,
  PageHeatmapResponseSchema,
  QueryBatchResponseSchema,
  type Environment,
  type FunnelPreviewRequest,
  type FunnelResult,
  type HeatmapDevice,
  type HeatmapPagesResponse,
  type HeatmapSnapshotResponse,
  type PageHeatmapResponse,
  type JobResponse,
  type QueryBatchRequest,
  type QueryBatchResponse,
  type Tile,
} from '@zelisight/contract';

import { useAuth } from '@/auth/AuthProvider';
import { useWorkspace } from '@/lib/workspace';
import { endpoints } from '@/api/endpoints';
import { ApiError, apiRequest, type TokenProvider } from '@/api/http';
import { qk } from '@/api/query-keys';
import {
  AttributionSchema,
  AudienceSchema,
  EventDetailSchema,
  KeysResponseSchema,
  LiveResponseSchema,
  MeSchema,
  OnboardingSchema,
  PeopleResponseSchema,
  PersonDetailSchema,
  PersonTimelineSchema,
  ProjectSettingsSchema,
  PropertyKeysResponseSchema,
  ReplayManifestSchema,
  RotateKeyResponseSchema,
  DeleteKeyResponseSchema,
  SessionListResponseSchema,
  SessionMetricsSchema,
  WriteKeySchema,
  type Attribution,
  type Audience,
  type EventDetail,
  type KeysResponse,
  type LiveResponse,
  type Me,
  type Onboarding,
  type PeopleResponse,
  type PersonDetail,
  type PersonTimeline,
  type ProjectSettings,
  type PropertyKeysResponse,
  type ReplayManifest,
  type RotateKeyResponse,
  type DeleteKeyResponse,
  type SessionListResponse,
  type SessionMetrics,
  type WriteKey,
} from '@/api/schemas';
import { browserTimeZone, type ResolvedRange } from '@/lib/range';

/**
 * `staleTime` matched to the source, per plan 5.10 and CLAUDE.md section 5:
 * thirty seconds for rollups, zero for the live tap, five minutes for anything
 * DuckDB answers.
 */
export const STALE_ROLLUP_MS = 30_000;
export const STALE_LIVE_MS = 0;
export const STALE_COLD_MS = 5 * 60_000;

/** The live tap poll interval, plan FR-DASH-3. */
export const LIVE_POLL_MS = 5_000;

/** The onboarding poll interval, plan FR-DASH-8. */
export const ONBOARDING_POLL_MS = 2_000;

/** Tile ids used by the Overview batch. Stable, because they are cache keys. */
export const OVERVIEW_TILES = {
  events: 'events_ts',
  users: 'users_ts',
  sessions: 'sessions',
  topEvents: 'top_events',
  recentSessions: 'recent_sess',
  botEvents: 'bot_events',
  totalEvents: 'events_total',
} as const;

function useToken(): TokenProvider {
  const { getIdToken } = useAuth();
  return useCallback(() => getIdToken(), [getIdToken]);
}

/**
 * Retry policy. A 401, 403 or 404 is an answer, not a hiccup, and retrying it
 * three times only delays the honest error state by two seconds.
 */
function retryTransientOnly(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError) return error.isRetryable && failureCount < 2;
  return false;
}

/* ----------------------------------------------------------------------- *
 * Identity
 * ----------------------------------------------------------------------- */

/**
 * `GET /v1/me` also performs the idempotent lazy bootstrap of Org, Default
 * project and three write keys (plan FR-AUTH-4), which is why it is the first
 * call the app makes and why nothing else may run before it resolves.
 */
export function useMe(): UseQueryResult<Me, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.me(),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.me(browserTimeZone()), MeSchema, getToken, { signal }),
    staleTime: STALE_ROLLUP_MS,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * Overview: one batched call, plan 5.10
 * ----------------------------------------------------------------------- */

export function buildOverviewRequest(
  env: Environment,
  range: ResolvedRange,
  exactUniques: boolean,
): QueryBatchRequest {
  const tiles: Tile[] = [
    { id: OVERVIEW_TILES.events, kind: 'timeseries', metric: 'events' },
    { id: OVERVIEW_TILES.users, kind: 'timeseries', metric: 'unique_persons' },
    { id: OVERVIEW_TILES.sessions, kind: 'scalar', metric: 'sessions' },
    { id: OVERVIEW_TILES.topEvents, kind: 'top', metric: 'events', limit: 10 },
    { id: OVERVIEW_TILES.recentSessions, kind: 'sessions', limit: 10 },
    // Both halves of the bot split, because the ratio is the point: `events`
    // includes bots and `unique_persons` excludes them, so without these two
    // the relationship between the headline numbers cannot be explained.
    { id: OVERVIEW_TILES.totalEvents, kind: 'scalar', metric: 'events' },
    { id: OVERVIEW_TILES.botEvents, kind: 'scalar', metric: 'bot_events' },
  ];
  const request: QueryBatchRequest = {
    env,
    from: range.from,
    to: range.to,
    granularity: range.granularity,
    tiles,
  };
  // Only sent when the user asked for it and the range allows it, so the
  // default path never routes to DuckDB.
  return exactUniques ? { ...request, exact_uniques: true } : request;
}

/**
 * Every Overview tile from one network call, which is what makes "no tile can
 * disagree with another" structurally true rather than a promise (FR-DASH-1,
 * acceptance A9.4).
 */
export function useOverview(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  exactUniques: boolean,
): UseQueryResult<QueryBatchResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.overview(projectId, env, range, exactUniques),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.queryBatch(projectId), QueryBatchResponseSchema, getToken, {
        method: 'POST',
        body: buildOverviewRequest(env, range, exactUniques),
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    // Charts hold their previous shape while a new range loads instead of
    // collapsing to a skeleton and back.
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/**
 * The Sessions screen reuses the batched query rather than adding a second
 * read surface: a `sessions` tile at the contract's maximum limit plus the
 * scalar count is exactly the same data path the Overview uses, so the two
 * screens cannot disagree (acceptance A9.5).
 */
export function useSessions(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  limit = 100,
): UseQueryResult<QueryBatchResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.sessions(projectId, env, range, limit),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.queryBatch(projectId), QueryBatchResponseSchema, getToken, {
        method: 'POST',
        body: {
          env,
          from: range.from,
          to: range.to,
          granularity: range.granularity,
          tiles: [
            { id: OVERVIEW_TILES.sessions, kind: 'scalar', metric: 'sessions' },
            { id: OVERVIEW_TILES.recentSessions, kind: 'sessions', limit },
          ],
        } satisfies QueryBatchRequest,
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/**
 * The contract's ceiling on a top tile. Asked for in full rather than a round
 * number: a name missing from the picker is a name somebody has to type from
 * memory, which is the problem this exists to remove.
 */
const EVENT_NAME_LIMIT = 100;

/**
 * Every event name in the project, for the range and environment on screen.
 *
 * A `top` tile rather than a new endpoint, and that is deliberate. The names,
 * their volumes, and their environment and range scoping already come out of
 * `query:batch`, so a dedicated catalogue would be a second source of truth for
 * the same list and would be free to disagree with the Overview's Top events
 * table.
 *
 * Ranked by volume, not alphabetically. The event you want in a funnel is far
 * more likely to be one of the busy ones, and the count beside each name is the
 * quickest way to notice you have picked the wrong one.
 */
export function useEventNames(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
): UseQueryResult<QueryBatchResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.eventNames(projectId, env, range),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.queryBatch(projectId), QueryBatchResponseSchema, getToken, {
        method: 'POST',
        body: {
          env,
          from: range.from,
          to: range.to,
          granularity: range.granularity,
          tiles: [
            { id: OVERVIEW_TILES.topEvents, kind: 'top', metric: 'events', limit: EVENT_NAME_LIMIT },
          ],
        } satisfies QueryBatchRequest,
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/** Tile ids for the activity batch. Stable, because they are cache keys. */
export const ACTIVITY_TILES = {
  mau: 'mau',
  dauSeries: 'dau_series',
} as const;

/** The rolling window MAU is defined over. Thirty days is the category default. */
export const MAU_WINDOW_DAYS = 30;

function daysAgo(days: number): string {
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - days);
  return from.toISOString();
}

/**
 * Daily and monthly active people.
 *
 * ITS OWN CALL, ON ITS OWN WINDOW, and that is the point rather than an
 * oversight. MAU means "unique in the last thirty days". If it were read from
 * the Overview's selected range it would silently become "unique in the last
 * seven days" the moment somebody narrowed the picker, and a stickiness ratio
 * computed from that is not a ratio of anything. The window is fixed here so
 * the number keeps its meaning whatever else the screen is showing.
 *
 * MAU is a union of the daily HyperLogLog sketches, not a sum of the daily
 * numbers. Summing would count somebody who visited on ten days as ten people,
 * which is the single most common way this metric is reported wrongly.
 */
export function useActivity(
  projectId: string,
  env: Environment,
): UseQueryResult<QueryBatchResponse, Error> {
  const getToken = useToken();
  const to = new Date().toISOString();
  const mauFrom = daysAgo(MAU_WINDOW_DAYS);
  return useQuery({
    queryKey: qk.activity(projectId, env, mauFrom.slice(0, 13)),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.queryBatch(projectId), QueryBatchResponseSchema, getToken, {
        method: 'POST',
        body: {
          env,
          from: mauFrom,
          to,
          granularity: 'day',
          tiles: [
            { id: ACTIVITY_TILES.mau, kind: 'scalar', metric: 'unique_persons' },
            { id: ACTIVITY_TILES.dauSeries, kind: 'timeseries', metric: 'unique_persons' },
          ],
        } satisfies QueryBatchRequest,
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * Live tap
 * ----------------------------------------------------------------------- */

export function useLive(
  projectId: string,
  env: Environment,
  after: string | null,
  enabled = true,
): UseQueryResult<LiveResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.live(projectId, env),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.live(projectId), LiveResponseSchema, getToken, {
        query: { env, after: after ?? undefined, limit: LIVE_FEED_LIMIT },
        signal,
      }),
    enabled,
    staleTime: STALE_LIVE_MS,
    refetchInterval: LIVE_POLL_MS,
    // Polling only while the tab is visible. A backgrounded dashboard polling
    // every five seconds for an hour is a bill with no reader.
    refetchIntervalInBackground: false,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * Event drilldown
 * ----------------------------------------------------------------------- */

export function useEventDetail(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  eventName: string,
  breakdown: readonly string[],
): UseQueryResult<EventDetail, Error> {
  const getToken = useToken();
  // The contract caps breakdowns at five properties. Truncating here means a
  // stale bookmark with six cannot produce a 400 the user has no way to fix.
  const limited = [...breakdown].slice(0, MAX_BREAKDOWN_PROPERTIES);
  return useQuery({
    queryKey: qk.eventDetail(projectId, env, range, eventName, limited),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.eventDetail(projectId, eventName), EventDetailSchema, getToken, {
        query: {
          env,
          from: range.from,
          to: range.to,
          granularity: range.granularity,
          breakdown: limited.length > 0 ? limited.join(',') : undefined,
        },
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/**
 * The property keys observed on this event, which is what the breakdown menu
 * offers. Separate from the drilldown so that toggling a breakdown does not
 * re-read the property key index.
 */
export function useEventProperties(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  eventName: string,
  enabled = true,
): UseQueryResult<PropertyKeysResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.eventProperties(projectId, env, range, eventName),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.eventProperties(projectId, eventName),
        PropertyKeysResponseSchema,
        getToken,
        { query: { env, from: range.from, to: range.to }, signal },
      ),
    enabled: enabled && eventName.length > 0,
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * People
 * ----------------------------------------------------------------------- */

/**
 * Which half of the person index to read.
 *
 * `identified` is the default because the anonymous rows outnumber the named
 * ones by roughly twenty to one and used to sort ahead of them, so the screen
 * opened on thirty two consecutive pages of "not identified".
 */
export type PeopleFilter = 'identified' | 'anonymous' | 'all';

const IDENTIFIED_PARAM: Record<PeopleFilter, string> = {
  identified: 'true',
  anonymous: 'false',
  all: 'all',
};

export function usePeople(
  projectId: string,
  env: Environment,
  search: string,
  filter: PeopleFilter,
  cursor: string | null,
): UseQueryResult<PeopleResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.people(projectId, env, search, filter, cursor),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.people(projectId), PeopleResponseSchema, getToken, {
        query: {
          env,
          q: search.length > 0 ? search : undefined,
          identified: IDENTIFIED_PARAM[filter],
          cursor: cursor ?? undefined,
        },
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

export function usePerson(
  projectId: string,
  distinctId: string,
  env: Environment,
): UseQueryResult<PersonDetail, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.person(projectId, distinctId, env),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.person(projectId, distinctId), PersonDetailSchema, getToken, {
        query: { env },
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    retry: retryTransientOnly,
  });
}

/**
 * DuckDB backed, so `meta.tier` is `cold` and the freshness stamp lags.
 *
 * `from` and `to` are required by the route: the timeline reads Parquet under a
 * date partitioned prefix, so an unbounded query would scan the whole project.
 * Omitting them answered 400 on every load of this screen.
 */
export function usePersonTimeline(
  projectId: string,
  distinctId: string,
  env: Environment,
  range: ResolvedRange,
  cursor: string | null,
): UseQueryResult<PersonTimeline, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.personTimeline(projectId, distinctId, env, range, cursor),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.personTimeline(projectId, distinctId), PersonTimelineSchema, getToken, {
        query: {
          env,
          from: range.from,
          to: range.to,
          cursor: cursor ?? undefined,
        },
        signal,
      }),
    staleTime: STALE_COLD_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/** Rows per page of the sessions list: the read plane's MAX_PAGE_LIMIT. */
export const SESSION_LIST_PAGE = 100;

/**
 * One page of the project's sessions, newest first.
 *
 * Keyset paged: `cursor` is what the previous page returned, `null` is the
 * first page. The server hands back a cursor only when there is more, so a
 * null cursor on the answer is the last page.
 */
export function useSessionPage(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  cursor: string | null,
): UseQueryResult<SessionListResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.sessionPage(projectId, env, range, cursor),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.sessions(projectId), SessionListResponseSchema, getToken, {
        query: {
          env,
          from: range.from,
          to: range.to,
          limit: String(SESSION_LIST_PAGE),
          ...(cursor === null ? {} : { cursor }),
        },
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/**
 * One person's sessions. A DynamoDB range read filtered to this person, which
 * is why it is a separate call from the timeline rather than a field on it: the
 * two have different tiers and different latencies, and pairing them would make
 * the fast one wait for the slow one.
 */
export function usePersonSessions(
  projectId: string,
  distinctId: string,
  env: Environment,
  range: ResolvedRange,
  cursor: string | null = null,
): UseQueryResult<SessionListResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: [...qk.personSessions(projectId, distinctId, env, range), { cursor }],
    queryFn: ({ signal }) =>
      apiRequest(endpoints.sessions(projectId), SessionListResponseSchema, getToken, {
        query: { env, from: range.from, to: range.to, distinct_id: distinctId, ...(cursor ? { cursor } : {}) },
        signal,
      }),
    staleTime: STALE_ROLLUP_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * Session replay
 * ----------------------------------------------------------------------- */

/**
 * Presigned chunk URLs for one session.
 *
 * `enabled` is what stops this firing for every row in the sessions table: the
 * manifest signs one URL per chunk, so fetching it speculatively would sign
 * thousands of URLs nobody asked for.
 *
 * `staleTime` is deliberately short relative to the URLs' own lifetime. A
 * manifest cached past its signatures produces a player that loads and then
 * silently shows nothing, which is the least debuggable failure available.
 */
export function useReplayManifest(
  sessionId: string | null,
  enabled: boolean,
): UseQueryResult<ReplayManifest, Error> {
  const getToken = useToken();
  const { project } = useWorkspace();
  return useQuery({
    queryKey: qk.sessionReplay(project.project_id, sessionId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.sessionReplay(project.project_id, sessionId ?? ''),
        ReplayManifestSchema,
        getToken,
        { signal },
      ),
    enabled: enabled && sessionId !== null && sessionId.length > 0,
    staleTime: 5 * 60_000,
    retry: retryTransientOnly,
  });
}

/**
 * How long after its newest chunk a session still counts as live.
 *
 * Chunks are cut every ten seconds, so a session that has produced nothing for
 * a minute has almost certainly ended: the tab was closed, or the person walked
 * away. Generous rather than tight, because calling a live session dead stops
 * the poll and needs a reopen to recover, while calling a dead session live
 * costs one more poll that returns nothing.
 */
export const LIVE_SESSION_WINDOW_MS = 60_000;

/** How often a live session is polled for new chunks. */
export const REPLAY_POLL_MS = 5_000;

export function isLive(latestChunkAt: string | null, now: number = Date.now()): boolean {
  if (latestChunkAt === null) return false;
  const at = Date.parse(latestChunkAt);
  if (Number.isNaN(at)) return false;
  return now - at <= LIVE_SESSION_WINDOW_MS;
}

/**
 * Chunks that have arrived since the player was given the last batch.
 *
 * Separate from {@link useReplayManifest} rather than folded into it, because
 * the two want opposite things from the cache. The initial manifest is the
 * whole recording and should be reused; a poll is a delta that is consumed once
 * and must never be replayed from cache, or the player would be handed the same
 * events twice and the timeline would gain duplicates.
 *
 * Polling stops on its own once a session goes quiet, so a viewer reviewing an
 * old recording issues exactly one request. The cursor is what keeps each poll
 * cheap: without it every poll resends the entire recording, and at roughly
 * 1.7 KB per presigned URL a two hour session is over a megabyte, several times
 * a minute, to one viewer.
 */
export function useReplayUpdates(
  sessionId: string | null,
  afterSequence: number | null,
  active: boolean,
): UseQueryResult<ReplayManifest, Error> {
  const getToken = useToken();
  const { project } = useWorkspace();
  return useQuery({
    queryKey: [
      ...qk.sessionReplay(project.project_id, sessionId ?? ''),
      'after',
      afterSequence,
    ],
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.sessionReplay(project.project_id, sessionId ?? '', afterSequence),
        ReplayManifestSchema,
        getToken,
        { signal },
      ),
    enabled:
      active && sessionId !== null && sessionId.length > 0 && afterSequence !== null,
    refetchInterval: REPLAY_POLL_MS,
    // A tab in the background is not being watched, so it does not need chunks.
    refetchIntervalInBackground: false,
    // Never serve a delta from cache. It has already been applied to the
    // player, and applying it again would duplicate events on the timeline.
    staleTime: 0,
    gcTime: 0,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * Onboarding
 * ----------------------------------------------------------------------- */

export function useOnboarding(
  projectId: string,
  poll: boolean,
): UseQueryResult<Onboarding, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.onboarding(projectId),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.onboarding(projectId), OnboardingSchema, getToken, { signal }),
    staleTime: STALE_LIVE_MS,
    refetchInterval: poll ? ONBOARDING_POLL_MS : false,
    // The onboarding panel is the one screen where a backgrounded tab must keep
    // polling: the customer has tabbed away to paste the snippet into their own
    // app, and coming back to a stale "waiting" panel would be the worst
    // possible first impression.
    refetchIntervalInBackground: poll,
    retry: retryTransientOnly,
  });
}

/* ----------------------------------------------------------------------- *
 * Project and write keys
 * ----------------------------------------------------------------------- */

export function useProjectSettings(
  projectId: string,
): UseQueryResult<ProjectSettings, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.settings(projectId),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.project(projectId), ProjectSettingsSchema, getToken, { signal }),
    staleTime: STALE_ROLLUP_MS,
    retry: retryTransientOnly,
  });
}

export function useRenameProject(
  projectId: string,
): UseMutationResult<ProjectSettings, Error, string> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiRequest(endpoints.project(projectId), ProjectSettingsSchema, getToken, {
        method: 'PATCH',
        body: { name },
      }),
    onSuccess: (updated) => {
      client.setQueryData(qk.settings(projectId), updated);
      // The project name is also rendered by the switcher, which reads /me.
      void client.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

/** The fields the replay card can change. Enabling requires the acknowledgement. */
export interface ReplaySettingsUpdate {
  replay_enabled?: boolean;
  replay_consent_acknowledged?: boolean;
  replay_sample_rate?: number;
}

/**
 * Turn session replay on or off, and set what fraction of sessions it records.
 *
 * `replay_consent_acknowledged` is not a formality and is not defaulted here.
 * FR-REP-2 makes enabling replay an explicit act: it records a customer's end
 * users inside their own browsers, and the acknowledgement is the point at
 * which an admin accepts that obtaining notice and consent is theirs to do and
 * that masking is client side and final. The API rejects an enable without it,
 * so sending it automatically would turn a deliberate gate into a checkbox
 * nobody ever sees.
 */
export function useUpdateReplaySettings(
  projectId: string,
): UseMutationResult<ProjectSettings, Error, ReplaySettingsUpdate> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: ReplaySettingsUpdate) =>
      apiRequest(endpoints.project(projectId), ProjectSettingsSchema, getToken, {
        method: 'PATCH',
        body: update,
      }),
    onSuccess: (updated) => {
      client.setQueryData(qk.settings(projectId), updated);
    },
  });
}

export function useKeys(projectId: string): UseQueryResult<KeysResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.keys(projectId),
    queryFn: ({ signal }) =>
      apiRequest(endpoints.keys(projectId), KeysResponseSchema, getToken, { signal }),
    staleTime: STALE_ROLLUP_MS,
    retry: retryTransientOnly,
  });
}

export interface CreateKeyInput {
  env: Environment;
  label: string;
}

export function useCreateKey(
  projectId: string,
): UseMutationResult<WriteKey, Error, CreateKeyInput> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKeyInput) =>
      apiRequest(endpoints.keys(projectId), WriteKeySchema, getToken, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.keys(projectId) });
    },
  });
}

/**
 * Rotation mints a second active key and moves the old one to `deprecated`
 * with a thirty day expiry (plan 5.1.1). Both work until the old one is
 * revoked, which is the whole point: instant revocation of an embedded key
 * breaks every cached client.
 */
export function useRotateKey(
  projectId: string,
): UseMutationResult<RotateKeyResponse, Error, string> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiRequest(endpoints.keyRotate(projectId, keyId), RotateKeyResponseSchema, getToken, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.keys(projectId) });
    },
  });
}

export function useRevokeKey(projectId: string): UseMutationResult<WriteKey, Error, string> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiRequest(endpoints.keyRevoke(projectId, keyId), WriteKeySchema, getToken, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.keys(projectId) });
    },
  });
}

/**
 * Removes a revoked key from the list. The server refuses a live key, so a key
 * always passes through the revoke warning before it can disappear.
 */
export function useDeleteKey(
  projectId: string,
): UseMutationResult<DeleteKeyResponse, Error, string> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) =>
      apiRequest(endpoints.key(projectId, keyId), DeleteKeyResponseSchema, getToken, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.keys(projectId) });
    },
  });
}

/**
 * Replace a key's allowed domains (ADR 0035). The server normalises what was
 * typed (a pasted URL becomes its host) and answers with the stored list.
 */
export function useSetKeyDomains(
  projectId: string,
): UseMutationResult<WriteKey, Error, { keyId: string; domains: string[] }> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, domains }) =>
      apiRequest(endpoints.key(projectId, keyId), WriteKeySchema, getToken, {
        method: 'PATCH',
        body: { allowed_origins: domains },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.keys(projectId) });
    },
  });
}

/* ------------------------------------------------------------------------- *
 * Funnels (FR-DASH-6).
 *
 * A funnel is answered by the cold tier as an async job: submit, then poll. It
 * cannot be a tile, because a counter knows how many and a sketch knows how many
 * distinct, and neither knows WHO or IN WHAT ORDER.
 * ------------------------------------------------------------------------- */

/** How often to ask whether a submitted funnel has finished. */
export const FUNNEL_POLL_MS = 1_500;

/**
 * How long to keep polling before giving up.
 *
 * The query Lambda's own ceiling is 300 seconds, so a job still running well
 * past that is not slow, it is gone: a timeout that never wrote a terminal
 * status, or a delivery that never arrived. Polling forever would leave the
 * screen spinning on a job nobody is working on.
 */
export const FUNNEL_POLL_TIMEOUT_MS = 330_000;

export function useSubmitFunnel(
  projectId: string,
): UseMutationResult<JobResponse, Error, FunnelPreviewRequest> {
  const getToken = useToken();
  return useMutation({
    mutationFn: (request: FunnelPreviewRequest) =>
      apiRequest(endpoints.funnelPreview(projectId), JobResponseSchema, getToken, {
        method: 'POST',
        body: request,
      }),
  });
}

/**
 * Poll one funnel job until it reaches a terminal status.
 *
 * `refetchInterval` returns false once the job is done or errored, which is what
 * stops the poll: a finished funnel that kept asking every 1.5 seconds would
 * bill a DynamoDB read per tick for as long as the tab stayed open.
 *
 * The result is NOT parsed here. `JobResponseSchema.result` is `unknown` because
 * the server returns it unvalidated and one endpoint serves every operation, so
 * the caller parses it with the schema for the operation it asked for. That is
 * also what stops a funnel screen rendering an export result.
 */
export function useFunnelJob(
  projectId: string,
  jobId: string | null,
): UseQueryResult<JobResponse, Error> {
  const getToken = useToken();
  const startedAt = useRef<number>(0);
  if (jobId !== null && startedAt.current === 0) startedAt.current = Date.now();
  if (jobId === null) startedAt.current = 0;

  return useQuery({
    queryKey: qk.funnelJob(projectId, jobId ?? 'none'),
    enabled: jobId !== null,
    queryFn: ({ signal }) =>
      apiRequest(endpoints.job(projectId, jobId as string), JobResponseSchema, getToken, {
        signal,
      }),
    staleTime: STALE_LIVE_MS,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'done' || status === 'error') return false;
      if (startedAt.current > 0 && Date.now() - startedAt.current > FUNNEL_POLL_TIMEOUT_MS) {
        return false;
      }
      return FUNNEL_POLL_MS;
    },
    retry: retryTransientOnly,
  });
}

/**
 * The funnel payload out of a finished job, or null while it is not ready.
 *
 * Parsing here rather than in the poll is what makes a shape mismatch a visible
 * error on one screen instead of a rejected response everywhere.
 */
export function funnelResultOf(job: JobResponse | undefined): FunnelResult | null {
  if (job === undefined || job.status !== 'done' || job.result == null) return null;
  const parsed = FunnelResultSchema.safeParse(job.result);
  return parsed.success ? parsed.data : null;
}


/**
 * Bounce rate and session duration for the range.
 *
 * `STALE_COLD_MS` because it is a cold scan, and the lake does not change
 * within the five minutes that buys: refetching sooner spends a DuckDB
 * invocation to redraw the same number.
 */
export function useSessionMetrics(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
): UseQueryResult<SessionMetrics, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.sessionMetrics(projectId, env, range),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.sessionMetrics(projectId) +
          `?env=${encodeURIComponent(env)}&from=${encodeURIComponent(
            range.from,
          )}&to=${encodeURIComponent(range.to)}`,
        SessionMetricsSchema,
        getToken,
        { signal },
      ),
    staleTime: STALE_COLD_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}


/**
 * Campaign, channel and referrer breakdowns for the range.
 *
 * `STALE_COLD_MS` for the same reason as the session metrics above: it is a
 * DuckDB scan of compacted Parquet, and the lake does not change inside the five
 * minutes that buys, so a shorter stale time spends a cold invocation redrawing
 * identical bars.
 *
 * One hook for all five breakdowns because the server answers them in one scan.
 * Splitting them into five hooks would be five cold invocations for one screen,
 * and each would carry its own coverage, so the cards could disagree about which
 * days they were based on.
 */
export function useAttribution(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  limit: number,
): UseQueryResult<Attribution, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.attribution(projectId, env, range, limit),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.attribution(projectId) +
          `?env=${encodeURIComponent(env)}&from=${encodeURIComponent(
            range.from,
          )}&to=${encodeURIComponent(range.to)}&limit=${String(limit)}`,
        AttributionSchema,
        getToken,
        { signal },
      ),
    staleTime: STALE_COLD_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/**
 * Audience: country, device, browser and the other per visit breakdowns.
 *
 * One hook, one cold scan, cached like attribution for the same reasons.
 */
export function useAudience(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  limit: number,
): UseQueryResult<Audience, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.audience(projectId, env, range, limit),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.audience(projectId) +
          `?env=${encodeURIComponent(env)}&from=${encodeURIComponent(
            range.from,
          )}&to=${encodeURIComponent(range.to)}&limit=${String(limit)}`,
        AudienceSchema,
        getToken,
        { signal },
      ),
    staleTime: STALE_COLD_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/* ------------------------------------------------------------------------- *
 * Heatmaps (ADR 0034).
 *
 * Cold tier answers, so they share the attribution caching rules. The range is
 * capped at HEATMAP_MAX_RANGE_DAYS by the API; the screen checks it first so a
 * long preset shows an explanation instead of a failed request.
 * ------------------------------------------------------------------------- */

export const HEATMAP_MAX_RANGE_DAYS = 31;

export function heatmapRangeTooLong(range: ResolvedRange): boolean {
  const span = new Date(range.to).getTime() - new Date(range.from).getTime();
  return span > HEATMAP_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
}

function heatmapQuery(env: Environment, range: ResolvedRange): string {
  return `?env=${encodeURIComponent(env)}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
}

export function useHeatmapPages(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  enabled: boolean,
): UseQueryResult<HeatmapPagesResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.heatmapPages(projectId, env, range),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.heatmapPages(projectId) + heatmapQuery(env, range),
        HeatmapPagesResponseSchema,
        getToken,
        { signal },
      ),
    enabled,
    staleTime: STALE_COLD_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

export function usePageHeatmap(
  projectId: string,
  env: Environment,
  range: ResolvedRange,
  path: string | null,
  device: HeatmapDevice,
): UseQueryResult<PageHeatmapResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.pageHeatmap(projectId, env, range, path ?? '', device),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.pageHeatmap(projectId) +
          heatmapQuery(env, range) +
          `&path=${encodeURIComponent(path ?? '')}&device=${encodeURIComponent(device)}`,
        PageHeatmapResponseSchema,
        getToken,
        { signal },
      ),
    enabled: path !== null && !heatmapRangeTooLong(range),
    staleTime: STALE_COLD_MS,
    placeholderData: keepPreviousData,
    retry: retryTransientOnly,
  });
}

/** Turn heatmap capture on or off for the project. Admin and owner only. */
export function useSetHeatmapsEnabled(
  projectId: string,
): UseMutationResult<ProjectSettings, Error, boolean> {
  const getToken = useToken();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      apiRequest(endpoints.project(projectId), ProjectSettingsSchema, getToken, {
        method: 'PATCH',
        body: { heatmaps_enabled: enabled },
      }),
    onSuccess: (updated) => {
      client.setQueryData(qk.settings(projectId), updated);
    },
  });
}

/**
 * The page snapshot the heatmap is drawn over. The presigned URL lives for an
 * hour, so the cache is refreshed well inside that.
 */
export function useHeatmapSnapshot(
  projectId: string,
  env: Environment,
  path: string | null,
  device: HeatmapDevice,
): UseQueryResult<HeatmapSnapshotResponse, Error> {
  const getToken = useToken();
  return useQuery({
    queryKey: qk.heatmapSnapshot(projectId, env, path ?? '', device),
    queryFn: ({ signal }) =>
      apiRequest(
        endpoints.heatmapSnapshot(projectId) +
          `?env=${encodeURIComponent(env)}&path=${encodeURIComponent(path ?? '')}&device=${encodeURIComponent(device)}`,
        HeatmapSnapshotResponseSchema,
        getToken,
        { signal },
      ),
    enabled: path !== null,
    staleTime: 20 * 60_000,
    // The presigned URL lives an hour. A tab left open must not end up handing
    // the player an expired one on its next layer switch.
    refetchInterval: 30 * 60_000,
    retry: retryTransientOnly,
  });
}
